import type { ModelType } from './models/ai';

export const COACH_AI_ID = 'coach-ai';
export const COACH_AI_PLAN_ID = 'coach-ai-plan';

export const DEFAULT_MODEL: ModelType = 'gemini-2.5-flash';
export const DEFAULT_TIMEZONE: string = 'Asia/Tokyo';

export const KNOWN_EVENT_METADATA_FIELDS = [
  { key: 'startLocation', icon: 'mdi-map-marker', label: 'Start Location' },
  { key: 'equipmentRequirements', icon: 'mdi-bag-personal', label: 'Equipment Requirements' },
  { key: 'officialDescription', icon: 'mdi-information-outline', label: 'Official Description' },
  { key: 'emergencyHotline', icon: 'mdi-phone', label: 'Emergency Hotline' },
  { key: 'essentialInfo', icon: 'mdi-alert-circle-outline', label: 'Essential Info' },
  { key: 'summary', icon: 'mdi-text-short', label: 'Summary' },
  { key: 'raceDayInfo', icon: 'mdi-calendar-clock', label: 'Race Day Info' },
  { key: 'routeInfo', icon: 'mdi-map-clock-outline', label: 'Route Info' },
  { key: 'raceDocumentCollectionInfo', icon: 'mdi-file-document-outline', label: 'Race Document Collection Info' },
  { key: 'name', icon: 'mdi-trophy', label: 'Name' },
  { key: 'date', icon: 'mdi-calendar', label: 'Date' },
];

export const DEFAULT_ADHOC_WEEKLY_PLAN_ID = 'ad-hoc-weekly-plan';
export const DEFAULT_VO2_MAX = 35; // Safe default VO2Max (average fitness level)
export const BASE_LONG_RUN_DURATION_SECONDS = 60 * 75; // 75 minutes in seconds
export const MAX_LONG_RUN_DURATION_SECONDS = 60 * 60 * 3; // 3 hours in seconds

export const SCHEDULES = {
  reportDailyV2: {
    cron: '5/30 * * * *',
    targetHour: 4, // 4am local time
  },
  reportWeekly: {
    cron: '15/30 * * * *',
    targetHour: 4, // 4am local time, right after the daily report
  },
  dailyTopic: {
    cron: '5/30 * * * *',
    targetHour: 6, // 6am local time
  },
  dailyTrainingPlan: {
    cron: '15/30 * * * *',
    targetHour: 4, // 4am local time, right after the daily report
  },
};

export const FIRST_RETRY_DELAY_MINUTES = 5;
export const RETRY_SCHEDULE_INTERVAL_MINUTES = 5;
export const MAX_RETRY_COUNT = 3;

export const DEFAULT_AUDIO_RECORDING_MAX_DURATION_SECONDS = 60 * 1; // 1 minutes in seconds
export const DEFAULT_AUDIO_RECORDING_MIME_TYPE = 'audio/webm';
export const DEFAULT_AUDIO_RECORDING_BITRATE = 48000; // 48 kbps
export const DEFAULT_TTS_AUDIO_BITRATE = 24000; // 24 kbps
export const DEFAULT_TTS_VOICE_ID = 'Laomedeia';

export const END_SEQUENCE = new Float32Array([
  // Random 16 Float32 values to represent the end of a stream, ensuring it's distinguishable from actual audio data
  0.123456789, -0.987654321, 0.555555555, -0.333333333, 0.111111111, -0.222222222, 0.333333333, -0.444444444,
  0.555555555, -0.666666666, 0.777777777, -0.888888888, 0.999999999, -0.111111111, 0.222222222, -0.333333333,
]); // A single sample of silence to indicate the end of a stream

export const INTERNAL_TESTING_ALLOWED_DOMAINS = ['@aix.sc', '@truffletechnologies.co.jp', '@ekiden.ai'];
export const TOKYO_MARATHON_CAMPAIGN_ACCESS_CLAIM = 'tokyoMarathonCampaignAccess';
export const TOKYO_MARATHON_OFFICIAL_URL = 'https://www.marathon.tokyo/en/';
export const TOKYO_MARATHON_RACE_DATE = '2027-03-07T00:00:00.000Z';

export const BEYOND_2026_CAMPAIGN_ACCESS_CLAIM = 'beyond2026CampaignAccess';
export const BEYOND_2026_OFFICIAL_URL = 'https://rslab.tokyo/beyond-2026/';
export const BEYOND_2026_RACE_DATE = '2026-12-29T00:00:00.000Z';

export const USAGE_PERIOD: number = 30; // roughly 5 * 30 = 150 message of 15000 tokens = 150 credits
export const EXPECTED_PRICE_PER_PERIOD: number = 10; // roughly 10 USD for 30 days usage
export const ROUGH_TOKEN_PER_MESSAGE: number = 15000; // roughly 1 message of 15000 tokens
export const EXPECTED_CREDIT_PER_MESSAGE: number = 1; // roughly 1 message of 15000 tokens = 1 credit
export const EXPECTED_MESSAGE_PER_DAY: number = 5; // roughly 5 message of 15000 tokens = 5 credits

// roughly 10 USD / (5 * 30) = 0.0666666667 USD per message of 15000 tokens
export const EXPECTED_PRICE_PER_MESSAGE: number = EXPECTED_PRICE_PER_PERIOD / (EXPECTED_MESSAGE_PER_DAY * USAGE_PERIOD);
// actual cost: 0,033USD per 1k tokens, so 0.033 * 15 = 0.495 USD per message of 15000 tokens
export const EXPECTED_MESSAGE_PER_PERIOD: number = EXPECTED_MESSAGE_PER_DAY * USAGE_PERIOD; // roughly 5 * 30 = 150 message of 15000 tokens = 150 credits
export const EXPECTED_CREDIT_PER_PERIOD: number = EXPECTED_CREDIT_PER_MESSAGE * EXPECTED_MESSAGE_PER_PERIOD; // roughly 5 * 30 = 150 message of 15000 tokens = 150 credits

export const TOKEN_PER_CREDIT: number = ROUGH_TOKEN_PER_MESSAGE * EXPECTED_CREDIT_PER_MESSAGE; // roughly 1 message of 15000 tokens = 1 credit
export const CREDIT_PER_TOKEN: number = 1 / TOKEN_PER_CREDIT;
export const CREDIT_PER_1M_TOKENS: number = 1000000 * CREDIT_PER_TOKEN;

export const CREDIT_PER_PERIOD: number = EXPECTED_CREDIT_PER_PERIOD; // 10 USD = 150 credits
export const TOKEN_PER_PERIOD: number = TOKEN_PER_CREDIT * CREDIT_PER_PERIOD; // 10 USD = 150 credits = 2,250,000 tokens

export const BASE_PRICE_PER_1M_TOKENS: number = 2.5;

export const EVENT_DATE_STR_FORMAT = 'yyyy/MM/dd';
